import ApiModel                    from '@widesk-core/models/ApiModel';
import View                        from '@widesk-ui/components/View';
import Button                      from '@widesk-ui/components/Button';
import Popover                     from '@widesk-ui/components/Popover';
import React                       from 'react';
import { observer }                from 'mobx-react-lite';
import { message }                 from '@widesk-ui/hooks/useMessage';
import { PictureOutlined }         from '@ant-design/icons';
import { EditOutlined }            from '@ant-design/icons';
import { CloseOutlined }           from '@ant-design/icons';
import { DownloadOutlined }        from '@ant-design/icons';
import { InboxOutlined }           from '@ant-design/icons';
import useDesignTokens             from '@/hooks/useDesignTokens';
import fileStore                   from '@/stores/fileStore';
import FileImage                   from '@/components/FileImage';
import updateSingleFileForModelUrn from '@/helpers/updateSingleFileForModelUrn';
import imageOf                     from '@/components/imageOf';
import storageImageCrossOrigin     from '@/helpers/storageImageCrossOrigin';
import { corsImageSrc }            from '@/helpers/storageImageCrossOrigin';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

/**
 * Image d'un objet, modifiable là où elle est affichée.
 *
 * Trois gestes sur la même vignette : la cliquer ouvre une zone de dépôt qui sert aussi de sélecteur
 * de fichier, y déposer une image directement la remplace, et une croix au survol la retire. Aucune
 * modale — c'est la direction prise par l'éditeur, et ça évite de quitter l'écran pour un geste
 * secondaire.
 *
 * Quatre pièges rencontrés en la construisant, tous commentés sur place, tous dus à des mécanismes
 * qui échouent en silence : `View` qui ne transmet ni `ref` ni les gestionnaires injectés par antd,
 * un `input` de fichier démonté pendant que la boîte de dialogue est ouverte, et le store des
 * fichiers qu'il ne faut pas interroger juste après l'avoir modifié.
 */
const EditableImage = observer((props: {
	model: ApiModel;
	reference: File_StonecodeFileTypeReference;
	/** Côté de la vignette, en pixels. */
	size?: number;
	radius?: number;
	/** Vignette seule, sans aucune prise : ni popover, ni survol, ni dépôt, ni sélecteur de fichier. */
	disabled?: boolean;
	/** Enveloppe d'enregistrement de l'écran hôte — l'indicateur "Enregistré" d'une sidebar. */
	onSave?: (task: () => Promise<void>) => Promise<void>;
	/** Appelé après un changement, pour rafraîchir ce qui affiche cette image ailleurs. */
	onChanged?: () => void;
}) => {
	const tokens = useDesignTokens();
	const inputRef = React.useRef<HTMLInputElement>(null);
	const [opened, setOpened] = React.useState(false);
	const [hover, setHover] = React.useState(false);
	const [dragging, setDragging] = React.useState(false);

	const size = props.size || 44;
	const file = imageOf(props.model, props.reference);

	/**
	 * Adresse affichable de l'image.
	 *
	 * Un fichier public s'affiche par son URL ; les autres sont servis par l'API et exigent les
	 * en-têtes d'authentification — on les récupère en base64, comme le fait `FileImage`, dont on
	 * emprunte le chargeur (et son cache).
	 *
	 * On rend l'image soi-même plutôt que d'utiliser `FileImage` : il passe un `placeholder` à AntD,
	 * qui le superpose à l'image jusqu'au `onLoad` — un événement qui n'arrive pas toujours avec une
	 * source `data:` et `loading="lazy"`. L'icône de montagne restait alors PAR-DESSUS l'image.
	 */
	const [src, setSrc] = React.useState<string>();

	React.useEffect(() => {
		if (!file) return setSrc(undefined);
		if (file.public) return setSrc(file.downloadPath);

		let abandonne = false;

		FileImage.fetchBase64ForFile(file)
			.then(base64 => !abandonne && setSrc(String(base64)))
			.catch(err => console.error('Image de ' + props.reference, err));

		return () => {
			abandonne = true;
		};
	}, [file?.id, file?.public, file?.downloadPath]);

	const run = async (task: () => Promise<void>) => {
		try {
			await (props.onSave ? props.onSave(task) : task());
		} catch (err) {
			message.exception(err);
		}
	};

	const replace = (picked: File) => {
		if (!ACCEPTED_IMAGE_TYPES.includes(picked.type)) {
			return message.error('Seules les images JPG et PNG sont acceptées.');
		}

		return run(async () => {
			const created = await updateSingleFileForModelUrn(props.model.urn, props.reference, picked, file);

			// On écrit ce qu'on vient de faire plutôt que de le redemander : le service de fichiers met un
			// instant à indexer un fichier neuf, et recharger rapportait la liste d'AVANT — donc l'image
			// précédente, réapparue à la place de celle qu'on venait de déposer.
			fileStore.forget(props.model, f => f.typeSource.type.reference === props.reference);

			if (created) fileStore.remember(props.model, created);

			props.onChanged?.();
		});
	};

	const remove = () => run(async () => {
		// `false` et non `undefined` : le premier demande la suppression, le second veut dire "ne touche
		// pas à l'image" (voir `updateSingleFileForModelUrn`).
		await updateSingleFileForModelUrn(props.model.urn, props.reference, false, file);

		// On OUBLIE le fichier au lieu de redemander la liste : elle nous revenait du cache du
		// connecteur, telle qu'elle était avant la suppression, et l'image réapparaissait.
		fileStore.forget(props.model, f => f.typeSource.type.reference === props.reference);

		setOpened(false);
		props.onChanged?.();
	});

	/**
	 * Le dépôt est accepté aux DEUX endroits qui l'annoncent : la vignette et la zone du popover.
	 *
	 * `dragover` doit annuler son comportement par défaut, sinon aucun `drop` n'est émis et le
	 * navigateur ouvre le fichier à la place. `stopPropagation` parce que l'éditeur écoute lui aussi
	 * les dépôts, pour importer une image sur la carte.
	 */
	const dropHandlers = {
		onDragOver: (e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setDragging(true);
		},
		onDragLeave: () => setDragging(false),
		onDrop: (e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setDragging(false);

			const dropped = e.dataTransfer.files?.[0];

			if (dropped) {
				setOpened(false);
				replace(dropped);
			}
		},
	};

	const visual = src ? (
		<img src={corsImageSrc(src)} alt="" crossOrigin={storageImageCrossOrigin(src)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
	) : (
		<View center heightF widthF color={tokens.text.muted}>
			<PictureOutlined />
		</View>
	);

	// Rendu à part plutôt qu'en neutralisant les quatre gestes un à un : en oublier un rouvrirait
	// l'édition.
	if (props.disabled) {
		return (
			<View
				w={size}
				h={size}
				br={props.radius ?? 8}
				bg={tokens.surface.subtle}
				style={{
					borderWidth: 1,
					borderStyle: 'solid',
					borderColor: tokens.border.default,
					overflow: 'hidden',
					flexShrink: 0,
				}}
			>
				{visual}
			</View>
		);
	}

	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			open={opened}
			onOpenChange={setOpened}
			content={(
				<View w={260} gap="sm">
					{/* Un `div` autour du visuel : `View` n'expose pas les événements de glisser, mais il
					    porte le survol et les jetons de couleur — chacun son rôle. */}
					<div {...dropHandlers}>
						<View
							center
							gap="xs"
							py="lg"
							px="md"
							br={8}
							pointer
							color={dragging ? tokens.color.primary : tokens.text.muted}
							hover={{ bg: tokens.surface.subtle }}
							style={{
								borderWidth: 1,
								borderStyle: 'dashed',
								borderColor: dragging ? tokens.color.primary : tokens.border.default,
							}}
							onClick={() => inputRef.current?.click()}
						>
							<InboxOutlined style={{ fontSize: 22 }} />

							<View size="small" center lineHeight={1.4}>Cliquez ou déposez une image</View>

							<View size={11}>JPG ou PNG</View>
						</View>
					</div>

					{!!src && (
						<View row>
							<Button type="link" size="small" danger style={{ padding: 0 }} onClick={remove}>
								Retirer l'image
							</Button>
						</View>
					)}
				</View>
			)}
		>
			{/* Un `div` et non un `View` : `View` n'expose pas les événements de glisser, et antd clone le
			    déclencheur d'un popover pour y injecter ses gestionnaires de clic — deux choses qu'un
			    `View` laisse tomber, sans erreur. */}
			<div style={{ display: 'flex', flexShrink: 0, padding: 8, margin: -8 }} {...dropHandlers}>
				<View
					rel
					w={size}
					h={size}
					br={props.radius ?? 8}
					pointer
					onHover={setHover}
					bg={tokens.surface.subtle}
					style={{
						// Propriétés séparées plutôt que le raccourci `border` : la couleur y est perdue.
						borderWidth: 1,
						borderStyle: dragging ? 'dashed' : 'solid',
						borderColor: dragging ? tokens.color.primary : tokens.border.default,
						overflow: 'hidden',
						flexShrink: 0,
					}}
				>
					{visual}

					{/* Survol : une image seule ne dit pas qu'elle se change. Pendant un glisser, la même
					    surface annonce le dépôt. Et tant que le popover est ouvert, pour qu'on voie d'où il
					    sort. */}
					{(hover || dragging || opened) && (
						<View
							absT={0}
							absL={0}
							widthF
							heightF
							center
							color="white"
							style={{ background: 'rgba(0, 0, 0, 0.45)' }}
						>
							{dragging ? <DownloadOutlined /> : <EditOutlined />}
						</View>
					)}

					{hover && !dragging && !!src && (
						<View
							absT={2}
							absR={2}
							center
							w={16}
							h={16}
							br="50%"
							size={9}
							color="white"
							style={{ background: 'rgba(0, 0, 0, 0.75)' }}
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								remove();
							}}
						>
							<CloseOutlined />
						</View>
					)}
				</View>

				{/* Hors du popover, exprès : ouvrir la boîte de dialogue du système peut le refermer, et un
				    `input` démonté pendant que l'utilisateur choisit son fichier n'émet jamais son `change`.
				    Vidé après chaque choix, sinon reprendre le MÊME fichier ne déclencherait rien. */}
				<input
					ref={inputRef}
					type="file"
					accept={ACCEPTED_IMAGE_TYPES.join(', ')}
					style={{ display: 'none' }}
					onChange={e => {
						const picked = e.target.files?.[0];

						e.target.value = '';

						if (picked) {
							setOpened(false);
							replace(picked);
						}
					}}
				/>
			</div>
		</Popover>
	);
});

export default EditableImage;
